import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { shouldUseSelfAssessmentRoutes } from '../../utils/userAccessHelpers';

/**
 * FullAccountRoute - Blocks users with organization package_tier from accessing full account routes
 * Redirects users with package_tier to /self-assessment/dashboard
 * 
 * Migration: Phase 8.4 - Uses package_tier instead of account_type
 */
const FullAccountRoute = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [isFullAccount, setIsFullAccount] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAccess = async () => {
      // CRITICAL: Check localStorage FIRST for immediate redirect
      try {
        const cachedUser = authService.getUser();
        if (cachedUser) {
          // Check if user should use self assessment routes (has package_tier)
          const isSelfAssessmentRouteUser = await shouldUseSelfAssessmentRoutes(cachedUser);
          if (isSelfAssessmentRouteUser) {
            setIsFullAccount(false);
            setChecking(false);
            return; // Exit early, don't wait for backend
          }
        }
      } catch (e) {
        // Continue to backend check
      }

      try {
        // Get fresh user data from backend
        const freshUser = await authService.getCurrentUser();
        
        if (!freshUser) {
          // No user data, redirect to signin
          setChecking(false);
          return;
        }

        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(freshUser));

        const isSelfAssessmentRouteUser = await shouldUseSelfAssessmentRoutes(freshUser);
        
        if (isSelfAssessmentRouteUser) {
          setIsFullAccount(false);
        } else {
          setIsFullAccount(true);
        }
      } catch (error) {
        console.error('Error checking package tier:', error);
        // On error, check localStorage as fallback
        try {
          const cachedUser = authService.getUser();
          if (cachedUser) {
            const isSelfAssessmentRouteUser = await shouldUseSelfAssessmentRoutes(cachedUser);
            if (isSelfAssessmentRouteUser) {
              setIsFullAccount(false);
            } else {
              // If we can't verify, allow access (full account routes are default)
              setIsFullAccount(true);
            }
          } else {
            setIsFullAccount(false);
          }
        } catch (e) {
          setIsFullAccount(false);
        }
      } finally {
        setChecking(false);
      }
    };

    checkAccess();
  }, [location.pathname]);

  if (checking) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // If not a full account, redirect to self assessment dashboard
  if (!isFullAccount) {
    return <Navigate to="/self-assessment/dashboard" replace />;
  }

  // Full account, allow access
  return children;
};

export default FullAccountRoute;


